
import { toast } from "react-toastify";

let toastInstanceSequence = 0;
let persistentAlertSequence = 0;
const persistentAlertListeners = new Set();
const pendingPersistentAlerts = [];

const normalizeIdSegment = (value, fallback) => {
  const normalized = String(value || fallback)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return normalized || fallback;
};

export const normalizeSeverity = severity => {
  const type = typeof severity === "string" ? severity.toUpperCase() : "";

  if (type === "OK") return "success";
  if (type === "SUCCESS") return "success";
  if (type === "INFORMATION") return "info";
  if (type === "WARN") return "warning";
  if (["ERROR", "WARNING", "INFO"].includes(type)) return type.toLowerCase();
  return "info";
};

const createPersistentAlertId = severity => {
  persistentAlertSequence += 1;
  return `persistent-alert-${normalizeIdSegment(severity, "info")}-${Date.now().toString(36)}-${persistentAlertSequence}`;
};

const publishPersistentAlert = alert => {
  if (persistentAlertListeners.size === 0) {
    pendingPersistentAlerts.push(alert);
    return;
  }

  persistentAlertListeners.forEach(listener => listener(alert));
};

export const subscribePersistentAlerts = listener => {
  persistentAlertListeners.add(listener);

  if (pendingPersistentAlerts.length > 0) {
    const pending = pendingPersistentAlerts.splice(0, pendingPersistentAlerts.length);
    pending.forEach(alert => listener(alert));
  }

  return () => persistentAlertListeners.delete(listener);
};

export const scopeAlertToRoute = (alert, route) => {
  if (alert?.persistenceScope === "global" || alert?.source === "sse") {
    return alert;
  }

  return { ...alert, route };
};

export const retainAlertsForRoute = (alerts, route) => (
  (alerts || []).filter(alert => (
    alert?.persistenceScope === "global" ||
    alert?.source === "sse" ||
    alert?.route === route
  ))
);

const createToastId = ({ domain, action, severity, correlationId }) => {
  toastInstanceSequence += 1;

  const instanceId = correlationId || `${Date.now().toString(36)}-${toastInstanceSequence}`;

  return [
    "toast",
    normalizeIdSegment(domain, "application"),
    normalizeIdSegment(action, "notification"),
    normalizeIdSegment(severity, "info"),
    normalizeIdSegment(instanceId, `instance-${toastInstanceSequence}`)
  ].join("-");
};

function CustomAlert(severity, message, options = {}) {
  if (!message) return;

  const type = normalizeSeverity(severity);
  const {
    domain = "application",
    action = "notification",
    correlationId,
    source = "application",
    persistentId,
    toastId: suppliedToastId,
    ...toastOptions
  } = options;
  const normalizedSource = String(source || "application").toLowerCase();
  const shouldPersist = type !== "success";
  const shouldToast = type === "success" || normalizedSource === "sse";
  const persistenceScope = normalizedSource === "sse" ? "global" : "route";

  if (shouldPersist) {
    publishPersistentAlert({
      id: persistentId || createPersistentAlertId(type),
      severity: type,
      type: type.toUpperCase(),
      msg: String(message),
      message: String(message),
      source: normalizedSource,
      persistenceScope,
      domain,
      action,
      correlationId,
      createdAt: Date.now()
    });
  }

  if (!shouldToast) return;

  const toastId = suppliedToastId || createToastId({
    domain,
    action,
    severity: type,
    correlationId
  });

  if (type === "error") return toast.error(message, { ...toastOptions, toastId });
  if (type === "warning") return toast.warning(message, { ...toastOptions, toastId });
  if (type === "info") return toast.info(message, { ...toastOptions, toastId });
  if (type === "success") return toast.success(message, { ...toastOptions, toastId });
}

export default CustomAlert;
